/**
 * Cookie consent management utilities
 * RGPD/CNIL compliant cookie consent handling
 */

const CONSENT_COOKIE_NAME = 'cookie-consent';
const CONSENT_DURATION_DAYS = 365; // 13 months max according to CNIL, we use 12 months

export type ConsentStatus = 'accepted' | 'declined' | null;

/**
 * Set cookie consent (client-side)
 * This cookie is essential and cannot be refused (required by RGPD to remember user choice)
 */
export function setCookieConsent(status: 'accepted' | 'declined'): void {
  // Store in cookie (essential cookie, always created)
  const expirationDate = new Date();
  expirationDate.setTime(expirationDate.getTime() + (CONSENT_DURATION_DAYS * 24 * 60 * 60 * 1000));
  const expires = expirationDate.toUTCString();
  
  document.cookie = `${CONSENT_COOKIE_NAME}=${status}; expires=${expires}; path=/; SameSite=Lax`;
  
  // Also store in localStorage for quick client-side access
  localStorage.setItem('cookie-consent', status);
}

/**
 * Get cookie consent (client-side)
 */
export function getCookieConsent(): ConsentStatus {
  // Try localStorage first (faster)
  const stored = localStorage.getItem('cookie-consent');
  if (stored === 'accepted' || stored === 'declined') {
    return stored;
  }
  
  // Fallback to cookie
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CONSENT_COOKIE_NAME) {
      if (value === 'accepted' || value === 'declined') {
        // Sync with localStorage
        localStorage.setItem('cookie-consent', value);
        return value;
      }
    }
  }
  
  return null;
}

/**
 * Check if cookies are allowed (client-side)
 */
export function areCookiesAllowed(): boolean {
  const consent = getCookieConsent();
  return consent === 'accepted';
}

/**
 * Get cookie consent from request (server-side)
 * Use this in middleware, API routes, etc.
 */
export function getCookieConsentFromRequest(cookieHeader: string | null): ConsentStatus {
  if (!cookieHeader) {
    return null;
  }
  
  const cookies = cookieHeader.split(';').map(c => c.trim());
  for (const cookie of cookies) {
    const [name, value] = cookie.split('=');
    if (name === CONSENT_COOKIE_NAME) {
      if (value === 'accepted' || value === 'declined') {
        return value;
      }
    }
  }
  
  return null;
}

/**
 * Check if cookies are allowed from request (server-side)
 */
export function areCookiesAllowedFromRequest(cookieHeader: string | null): boolean {
  const consent = getCookieConsentFromRequest(cookieHeader);
  return consent === 'accepted';
}


